import * as React from 'react';
import i18n from 'i18next';
import _set from 'lodash/set';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';

// components
import { Button, Flexbox, IconButton } from 'components/spaceKit';
import ActionColorSwatchGroup from 'components/_UI/Forms/Actions/ActionColorSwatchGroup';
import { Collapse } from '../../../spaceKit';
import ActionSegmentedGroup from '../../../_UI/Forms/Actions/ActionSegmentedGroup';
import LabelGroup from '../../../_UI/Forms/LabelGroup/LabelGroup';
import ActionSliderGroup from '../../../_UI/Forms/Actions/ActionSliderGroup';

// constants
import { BrandColorsArray } from '@sprinklr/display-builder/stores/PanelTemplatesStore/PanelTemplatesTheme/constants';

// utils
import { updateWidgetOptions } from 'utils/Widget/WidgetUtils';
import {
    StatusTag,
    StatusTagConditionMetric,
} from '@sprinklr/display-builder/widgets/utils/getStatusTag';
import { getNewConditionValues } from './utils/getValues';
import { getMetricDesignOptionsKey } from '../metricDesignTunables/utils/getMetricDesignOptionsKey';
import { Condition } from './condition/Condition';

//types
import { StatusTagTunablesProps } from './types';
import { MultiMetricWidgetOptions } from '../../../../widgets/MultiMetricWidget/options';
import ActionCheckboxSliderGroup from '../../../_UI/Forms/Actions/ActionCheckboxSliderGroup';

export const formatOptions = [
    { value: 'color', label: i18n.t('Color'), icon: '' },
    { value: 'dot', label: i18n.t('Dot'), icon: '' },
];

function StatusTagTunables({ widget, projection, rootOption = false }: StatusTagTunablesProps) {
    const metricKey = getMetricDesignOptionsKey(
        projection?.measurementName,
        projection?.aggregateFunction
    );
    const options = rootOption
        ? widget?.options
        : widget?.options?.metricDesignOptions?.[metricKey];

    const { statusTags }: { statusTags: StatusTag[] } =
        options || ({} as { statusTags: StatusTag[] });

    const addRule = action(() => {
        const lastRule = statusTags?.at(-1);
        const lastCondition = lastRule?.conditions?.at(-1);

        const newTag = observable<StatusTag>({
            color:
                BrandColorsArray.slice()
                    .reverse()
                    .find(color => !statusTags?.find(statusTag => statusTag.color === color)) ||
                lastRule?.color,
            conditions: [
                {
                    type: 'METRIC',
                    name: metricKey,
                    ...getNewConditionValues(lastCondition),
                } as StatusTagConditionMetric,
            ],
            type: 'color',
            size: widget.options.metricDesignOptions?.[metricKey]?.size || 5,
            animation: {
                duration:
                    widget.options.metricDesignOptions?.[metricKey]?.animation?.duration || 2500,
                enabled: true,
            },
        });

        if (statusTags) {
            statusTags.push(newTag as any);
        } else {
            const newStatusTags = observable([newTag]);
            if (rootOption) {
                updateWidgetOptions(widget, `statusTags`, newStatusTags);
            } else {
                updateWidgetOptions(
                    widget,
                    `metricDesignOptions.${metricKey}.statusTags`,
                    newStatusTags
                );
                updateWidgetOptions(widget, `metricDesignOptions.${metricKey}.size`, 5);
                updateWidgetOptions(
                    widget,
                    `metricDesignOptions.${metricKey}.animation.duration`,
                    2500
                );
                updateWidgetOptions(
                    widget,
                    `metricDesignOptions.${metricKey}.animation.enabled`,
                    true
                );
            }
        }
    });

    const addCondition = action((index: number) => {
        const lastCondition = statusTags[index].conditions.at(-1);

        const newCondition = observable({
            type: 'METRIC',
            name: metricKey,
            ...getNewConditionValues(lastCondition),
        });
        statusTags[index].conditions.push(newCondition as any);
    });

    const removeRule = (index: number) => {
        runInAction(() => statusTags.splice(index, 1));
    };

    const handleSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        statusTags?.length &&
            statusTags.forEach(tag => {
                tag.size = parseFloat(event.target.value);
            });
        if (!rootOption) {
            updateWidgetOptions(
                widget,
                `metricDesignOptions.${metricKey}.size`,
                parseFloat(event.target.value)
            );
        } else {
            updateWidgetOptions(widget, 'statusTags', statusTags);
        }
    };

    const handleAnimationDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        statusTags?.length &&
            statusTags.forEach(tag => {
                tag.animation.duration = parseFloat(event.target.value);
            });
        if (!rootOption) {
            updateWidgetOptions(
                widget,
                `metricDesignOptions.${metricKey}.animation.duration`,
                parseFloat(event.target.value)
            );
        } else {
            updateWidgetOptions(widget, 'statusTags', statusTags);
        }
    };

    const handleAnimationEnable = (event: React.ChangeEvent<HTMLInputElement>) => {
        statusTags?.length &&
            statusTags.forEach(tag => {
                tag.animation.enabled = event.target.checked;
            });
        if (!rootOption) {
            updateWidgetOptions(
                widget,
                `metricDesignOptions.${metricKey}.animation.enabled`,
                event.target.checked
            );
        } else {
            updateWidgetOptions(widget, 'statusTags', statusTags);
        }
    };

    return (
        <>
            <Collapse label={i18n.t('Status Tags')} gap='xl'>
                <Collapse label={'Rules'}>
                    {statusTags?.map((set, index) => {
                        return (
                            <Flexbox key={index} middle vertical gap='ml'>
                                <Flexbox left middle gap='ml'>
                                    <LabelGroup label={i18n.t('Color')} key='set.color'>
                                        <ActionColorSwatchGroup value='color' option={set} />
                                    </LabelGroup>
                                    <LabelGroup
                                        label={i18n.t('Format')}
                                        key='widget.options.statusTags'>
                                        <ActionSegmentedGroup
                                            key='set.type'
                                            options={formatOptions}
                                            option={set}
                                            value='type'
                                            data-testid='status-tag-format-type'
                                            showOptionLabel={true}
                                        />
                                    </LabelGroup>
                                    <IconButton
                                        icon='icon-minus'
                                        onClick={() => removeRule(index)}
                                        data-testid='icon-button-remove-color-tag-rule'
                                    />
                                </Flexbox>

                                {set.conditions?.map((condition, conditionIndex) => {
                                    return (
                                        <Condition
                                            key={conditionIndex}
                                            condition={condition}
                                            tag={set}
                                            index={conditionIndex}
                                            widget={widget}
                                        />
                                    );
                                })}

                                <Button type='secondary' onClick={() => addCondition(index)}>
                                    {i18n.t('Add Condition')}
                                </Button>
                            </Flexbox>
                        );
                    })}

                    <IconButton
                        icon='icon-plus'
                        onClick={addRule}
                        data-testid='icon-button-add-color-set'
                    />
                </Collapse>
                {statusTags?.length ? (
                    <Collapse label={'Styles'} data-testid='collapse-styles-status-tag-tunables'>
                        <ActionSliderGroup
                            key='widget.options.statusTags.size'
                            label={i18n.t('Size')}
                            option={
                                (widget.options as MultiMetricWidgetOptions).metricDesignOptions?.[
                                    metricKey
                                ] || statusTags?.[0]
                            }
                            value='size'
                            showValue
                            min={1}
                            max={10}
                            onChange={handleSizeChange}
                        />
                        <ActionCheckboxSliderGroup
                            data-testid='checkbox-slider-animation-status-tag-tunables'
                            key='widget.options.statusTags.animation'
                            label={i18n.t('Animation')}
                            checkbox={{
                                option:
                                    (widget.options as MultiMetricWidgetOptions)
                                        .metricDesignOptions?.[metricKey].animation ||
                                    statusTags?.[0]?.animation,
                                value: 'enabled',
                                onChange: handleAnimationEnable,
                            }}
                            slider={{
                                option:
                                    (widget.options as MultiMetricWidgetOptions)
                                        .metricDesignOptions?.[metricKey].animation ||
                                    statusTags?.[0]?.animation,
                                value: 'duration',
                                min: 1000,
                                max: 5000,
                                onChange: handleAnimationDurationChange,
                            }}
                            showValue
                            suffix={'ms'}
                        />
                    </Collapse>
                ) : null}
            </Collapse>
        </>
    );
}

export default observer(StatusTagTunables);
